import moment from "moment";
import { BookingTypeEnum } from "./enums/booking-type-enum";
import { CancellationTypeEnum } from "./enums/cancellation-type-enum";
import { serviceTypeEnum } from "./enums/service-type-enum";
import { getOptionNameFromServiceName } from "./constants";
import { formatMessage } from "../translations/format-message";
import toast from "react-hot-toast";

type ServiceRequest = {
  ServiceRequestID: number;
  ServiceId: number;
  ServiceType: number;
  ServiceName: string;
  TotalTime: number;
};

type Appointment = {
  CustomerName: string;
  Time: string;
  StartTime: string;
  EndTime: string;
  TotalTime: number;
  BookingType: number;
  CancellationType: number;
  PostBreakTime: number;
  AppointmentStatus: number;
  Date: string;
  EventType: number;
  IsNominationFee: boolean;
  NominationFee: number;
  StaffPreference: number;
  ServiceRequestedList: ServiceRequest[];
};

export function downloadAppointmentsCSV(
  data: any[],
  filename: string = "appointments.csv"
) {
  if (!data.length) {
    toast.error(formatMessage("no_reservation_to_export"));
    return;
  }

  // Prepare CSV headers (ServiceNames is a single column)
  const headers = [
    formatMessage("reservation_date_time"),
    formatMessage("reservation_details"),
    formatMessage("reservation_amount"),
    formatMessage("diana_code"),
    formatMessage("name"),
    formatMessage("contact_number"),
    formatMessage("email"),
    formatMessage("reservation_notes"),
    formatMessage("main_staff"),
    formatMessage("with_without_staff_request"),
    formatMessage("number_of_visits"),
    formatMessage("reservation_confirmation_date"),
    formatMessage("reservation_source"),
    formatMessage("cancellation_category"),
  ];

  // Prepare CSV rows
const rows = data.map((item) => {
  const serviceNames =
    item.ServiceRequestedList && item.ServiceRequestedList.length
      ? item.ServiceRequestedList
          .map((s: any) =>
            s.ServiceType == serviceTypeEnum.Option
              ? getOptionNameFromServiceName(s.ServiceName)
              : s.ServiceName
          )
          .join(" / ")
      : "";

  let memoValue = "";
  if (item.Memo) {
    memoValue = item.Memo
      .replace(/"/g, "")           // remove all double quotes
      .replace(/(\r\n|\n|\r)/gm, " ") // replace line breaks with spaces
      .trim();
    memoValue = `"${memoValue}"`;  // manually wrap with one pair of quotes
  }

  return [
    `${moment(item?.Date).format("YYYY/MM/DD")} ${item.Time}`,
    serviceNames,
    item.TotalAmount,
    item.DianaCode,
    item.CustomerName,
    item.Phone1,
    item.Email,
    memoValue,
    item.StaffName,
    (item.StaffPreference == 0 ||
      item.StaffPreference == 2 ||
      item.StaffPreference == 1)
      ? formatMessage("no_staff_preference")
      : formatMessage("yes_staff_preference"),
    item.VisitsCount,
    `${moment(item?.Date).format("YYYY/MM/DD")} ${item.Time}`,
    ["NewOnCall", "NewVisit", "Visit", "OnCall"].includes(
      BookingTypeEnum[item.BookingType]
    )
      ? formatMessage("Store")
      : formatMessage("Customer"),
    CancellationTypeEnum.None !== item.CancellationType
      ? formatMessage(CancellationTypeEnum[item.CancellationType] + "_cancel")
      : "",
  ];
});

const csv = [
  headers.join(","),
  ...rows.map((row) =>
    row
      .map((value, index) => {
        if (index === 7) return value;
        return typeof value === "string" &&
          (value.includes(",") || value.includes('"') || value.includes("\n"))
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      })
      .join(",")
  ),
].join("\r\n");

  // Create file and trigger download (add UTF-8 BOM for Excel compatibility)
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  toast.success(formatMessage("downloaded_started_successfully"));
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

// Usage:
// downloadAppointmentsCSV(yourArray);
